import { EnvironmentProviders, Injectable, Provider, inject, provideAppInitializer } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { Translation, TranslocoLoader, TranslocoService, provideTransloco } from '@jsverse/transloco';

import { environment } from 'src/environments/environment';

/**
 * i18n for the hosted Chat product surface (src/app/chat/**, lifted from the
 * chat-ui repo). The chat feature keeps its transloco setup so its templates
 * (42 files using *transloco) and specs port unchanged; connect's
 * ngx-translate remains the app-wide source of truth for the ACTIVE language
 * and the bridge below keeps transloco following it.
 *
 * Convergence to a single i18n library is deliberate deferred debt.
 */
@Injectable({ providedIn: 'root' })
export class ChatTranslocoHttpLoader implements TranslocoLoader {
  private http = inject(HttpClient);

  getTranslation(lang: string) {
    return this.http.get<Translation>(`./assets/i18n/chat/${lang}.json`);
  }
}

function normalizeChatLanguage(lang: string | null | undefined): 'en' | 'fr' {
  return lang && lang.toLowerCase().startsWith('fr') ? 'fr' : 'en';
}

export function provideChatTransloco(): (Provider | EnvironmentProviders)[] {
  return [
    provideTransloco({
      config: {
        availableLangs: ['en', 'fr'],
        // Connect's fallback is 'en' (chat standalone defaulted to 'fr');
        // the active lang is bridged from ngx-translate below.
        defaultLang: 'en',
        reRenderOnLangChange: true,
        prodMode: environment.production,
      },
      loader: ChatTranslocoHttpLoader,
    }),
    provideAppInitializer(() => {
      const translate = inject(TranslateService);
      const transloco = inject(TranslocoService);

      // Initial sync: LanguageChangeService has already resolved the app
      // language (localStorage -> crewdle_lang cookie) by the time
      // AppComponent calls translate.use(); currentLang may not be set yet at
      // init time, so seed from the same storage and then follow every change.
      try {
        const stored = localStorage.getItem('lang');
        transloco.setActiveLang(normalizeChatLanguage(stored ?? translate.currentLang));
      } catch {
        transloco.setActiveLang(normalizeChatLanguage(translate.currentLang));
      }

      translate.onLangChange.subscribe((event) => {
        transloco.setActiveLang(normalizeChatLanguage(event.lang));
      });
    }),
  ];
}
